import { useEffect } from "react";

// "unknown" earlier than this means the popup never opened, but only when
// FB.login did not call window.open. A captured popup is a user cancel.
const POPUP_BLOCKED_ELAPSED_MS = 1500;
const POPUP_CLOSED_GRACE_MS = 3000;
const POPUP_POLL_MS = 500;
const LOGIN_SAFETY_TIMEOUT_MS = 5 * 60 * 1000;

let metaSdkStatus = "loading";

export const getMetaSdkStatus = () => metaSdkStatus;

export const getIsMetaSdkLoginReady = () =>
  metaSdkStatus === "ready" && typeof window.FB?.login === "function";

export const loadMetaFacebookSdk = () => {
  ((d, s, id) => {
    const fjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) {
      return;
    }
    const js = d.createElement(s);
    js.id = id;
    js.src = "https://connect.facebook.net/en_US/sdk.js";
    js.onerror = () => {
      metaSdkStatus = "error";
    };
    fjs.parentNode.insertBefore(js, fjs);
  })(document, "script", "facebook-jssdk");

  window.fbAsyncInit = () => {
    try {
      window.FB.init({
        appId: import.meta.env?.VITE_FACEBOOK_APP_ID,
        xfbml: true,
        version: "v24.0",
      });
      metaSdkStatus = "ready";
    } catch {
      metaSdkStatus = "init_error";
    }
  };
};

// Must stay synchronous: FB.login has to run inside the click gesture.
export const createMetaLoginMonitor = ({
  onCallbackFailed = () => {},
  onCallbackCancelled = () => {},
}) => {
  let settled = false;
  let responded = false;
  let popupRef;
  let isPopupCaptured = false;
  let startedAt = 0;
  let closedAt = null;
  let pollID = null;
  let safetyID = null;

  const settle = () => {
    if (settled) return false;
    settled = true;
    clearInterval(pollID);
    clearTimeout(safetyID);
    return true;
  };

  const fail = (mode) => {
    if (settle()) onCallbackFailed(mode);
  };

  const startWatchers = () => {
    if (isPopupCaptured) {
      pollID = setInterval(() => {
        if (!popupRef.closed) return;
        closedAt ??= Date.now();
        if (Date.now() - closedAt >= POPUP_CLOSED_GRACE_MS) {
          fail("popup_no_callback");
        }
      }, POPUP_POLL_MS);
    }
    safetyID = setTimeout(() => {
      if (settle()) onCallbackCancelled();
    }, LOGIN_SAFETY_TIMEOUT_MS);
  };

  return {
    runLogin: (loginFn) => {
      const originalOpen = window.open;
      window.open = (...args) => {
        popupRef = originalOpen.apply(window, args);
        isPopupCaptured = true;
        return popupRef;
      };
      startedAt = Date.now();
      try {
        loginFn();
      } catch {
        fail("sdk_login_threw");
        return;
      } finally {
        window.open = originalOpen;
      }
      if (settled) return;
      if (isPopupCaptured && (!popupRef || popupRef.closed)) {
        fail("popup_blocked");
        return;
      }
      startWatchers();
    },
    // True when the response is a login result the caller must handle.
    // A late login result is still handled after a timeout or popup alert.
    onResponse: (response) => {
      if (responded) return false;
      responded = true;
      if (response?.status !== "unknown") {
        settle();
        return true;
      }
      if (
        !isPopupCaptured &&
        Date.now() - startedAt < POPUP_BLOCKED_ELAPSED_MS
      ) {
        fail("popup_blocked");
        return false;
      }
      if (settle()) onCallbackCancelled();
      return false;
    },
  };
};

const MetaSDKLoader = () => {
  useEffect(() => {
    loadMetaFacebookSdk();
  }, []);
  return null;
};

export default MetaSDKLoader;
